/**
 * Page Controller Framework — shared types.
 *
 * The page-controller framework is the cross-tab/cross-device cousin of
 * `lib/page-state/`. Where `page-state` is an in-tab event emitter that
 * routes chat tool dispatches into the currently mounted page, the
 * page-controller framework uses Supabase Realtime `broadcast` channels
 * keyed by workspace + page. That means:
 *
 *   - Chat tools running on the server can mutate page state without
 *     needing the same React tree.
 *   - Two tabs of the same page stay in sync.
 *   - A chat action on the user's phone can drive the desktop dashboard.
 *
 * Channel naming convention: `page-controller:${workspaceId}:${pageId}`.
 * Example: `page-controller:abc-123:traffic`.
 *
 * No DB writes — pure broadcast messages. Each message carries a stable
 * `messageId` so clients can dedupe their own dispatches that loop back
 * through the channel.
 */

export type PageControllerSource = 'ai' | 'user' | 'system'

export interface PageControllerMessage {
  /** Page identifier, e.g. `'traffic'`. Matches the second segment of the channel name. */
  page: string
  /** Action verb the reducer will dispatch on, e.g. `'set_metric'`. */
  action: string
  /** Action-specific payload. Schema is owned by the per-page reducer. */
  payload: Record<string, unknown>
  /** Stable id so clients can dedupe their own echoed messages. */
  messageId: string
  /** Source of the action — for chat-AI vs user attribution + dedupe. */
  source: PageControllerSource
  /** Server-side timestamp (ms epoch). */
  ts: number
}

/**
 * Build the canonical channel name for a (workspace, page) pair.
 *
 * Centralised so server (`pushPageMessage`) and client (`usePageController`)
 * can never drift. Workspace id is normalised to lowercase to defend
 * against any cookie-case oddities; page is expected to already be a
 * stable lowercase slug.
 */
export function pageControllerChannelName(
  workspaceId: string,
  page: string,
): string {
  return `page-controller:${workspaceId.toLowerCase()}:${page}`
}
